'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const freeFeatures = [
  'Create 1 pet profile',
  'Basic NFC tag support',
  'Community feed access',
  'Lost & Found alerts',
  'Basic health records',
];

const premiumFeatures = [
  'Unlimited pet profiles',
  'Advanced NFC tag features',
  'AI Pet Assistant (PetBot)',
  'Priority vet directory',
  'Full health & diary tracking',
  'Premium academy courses',
  'Exclusive community groups',
  'Affiliate discount codes',
];

export default function LandingPricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section className="py-16 lg:py-20 section-alt">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="eyebrow text-muted-foreground">Simple Pricing</span>
          <h2 className="text-h2 text-foreground mt-2">Choose Your Plan</h2>
          <p className="text-body-text text-sm mt-2 font-medium">
            Start free — upgrade when you need more for your furbaby.
          </p>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm font-semibold ${!annual ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
            <label className="toggle-switch cursor-pointer" aria-label="Toggle annual billing">
              <input
                type="checkbox"
                checked={annual}
                onChange={() => setAnnual(!annual)}
                className="sr-only"
              />
              <span className="toggle-slider" style={{ backgroundColor: annual ? 'var(--secondary)' : undefined }}></span>
            </label>
            <span className={`text-sm font-semibold ${annual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual{' '}
              <span className="text-secondary text-xs font-bold bg-secondary-tint px-2 py-0.5 rounded-full ml-1">
                Save 45%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
          {/* Free */}
          <div className="bg-card rounded-xl border border-border/60 shadow-card p-7 flex flex-col gap-5">
            <div>
              <span className="eyebrow text-muted-foreground">Free Plan</span>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-4xl font-extrabold text-foreground font-tabular">₱0</span>
                <span className="text-sm text-muted-foreground font-medium">forever</span>
              </div>
              <p className="text-sm text-body-text mt-1 font-medium">Perfect for getting started with your first furbaby.</p>
            </div>

            <ul className="flex flex-col gap-2.5">
              {freeFeatures?.map((f) => (
                <li key={`free-feat-${f}`} className="flex items-center gap-2.5 text-sm text-body-text font-medium">
                  <Icon name="CheckIcon" size={16} className="text-secondary flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Link href="/sign-up-login-screen" className="btn-outline no-underline justify-center mt-auto">
              Get Started Free
            </Link>
          </div>

          {/* Premium */}
          <div className="bg-navy rounded-xl shadow-float p-7 flex flex-col gap-5 relative overflow-hidden">
            {/* Most loved badge */}
            <div className="absolute top-4 right-4 bg-yellow text-navy text-xs font-extrabold px-3 py-1 rounded-full">
              MOST LOVED ⭐
            </div>

            <div>
              <span className="eyebrow text-[#B9C3D6]">Premium Plan</span>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-4xl font-extrabold text-white font-tabular">
                  {annual ? '₱300' : '₱55'}
                </span>
                <span className="text-sm text-[#B9C3D6] font-medium">
                  {annual ? '/year' : '/month'}
                </span>
              </div>
              {annual && (
                <p className="text-xs text-yellow font-semibold mt-1">₱25/month billed annually</p>
              )}
              <p className="text-sm text-[#B9C3D6] mt-1 font-medium">Everything you need for the ultimate pet care experience.</p>
            </div>

            <ul className="flex flex-col gap-2.5">
              {premiumFeatures?.map((f) => (
                <li key={`prem-feat-${f}`} className="flex items-center gap-2.5 text-sm text-white font-medium">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-yellow flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <Link href="/sign-up-login-screen" className="no-underline justify-center mt-auto inline-flex items-center gap-2 bg-yellow hover:bg-yellow-hover text-navy font-bold text-sm px-6 py-3.5 rounded-full transition-colors">
              <Icon name="StarIcon" size={16} variant="solid" />
              Start Premium
            </Link>
          </div>
        </div>

        {/* Activation note */}
        <p className="text-center text-xs text-muted-foreground mt-6 font-medium">
          One-time Account Activation: ₱75 (lifetime) · Proceeds support{' '}
          <span className="text-secondary font-semibold">BACH Project Ph</span> 🐾
        </p>
      </div>
    </section>
  );
}